import React, { memo, useCallback } from 'react';
/** REDUX */
import { useDispatch } from 'react-redux';
import { deleteDictionaryItemAction } from '../../../redux/slices/dictionary';
import { deleteAnalysisIncidentAction } from '../../../redux/slices/analyses';
/** APOLLO */
import { useMutation } from '@apollo/client';
import incidentDeleteMutation from '../../../pages/Incidents/apollo/incidentDeleteMutation';
import {
  IIncidentDeleteMutation,
  IIncidentDeleteMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
/** COMPONENTS */
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import { assertNotNil } from '../../../utils/types';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import ConfirmationModal from '../../ConfirmationModal';
import { useAppSelector } from '../../../redux/hooks';
import { selectModalData } from '../../../redux/selectors/modal';
import { deleteIncidentAction } from '../../../redux/actions/incidents';
import toastMessage from '../../../utils/toastMessage';
import { closeModal } from '../../../redux/slices/modal';

interface IProps {
  analysisId?: string;
}

export default memo(function IncidentDeleteModal({ analysisId }: IProps) {
  const dispatch = useDispatch();
  const modalData = useAppSelector(selectModalData);
  const currentIncident = modalData.incident;
  assertNotNil(currentIncident);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const onDeleteEvent = useCallback(
    ({ incidentDelete }: IIncidentDeleteMutation) => {
      const { errors } = incidentDelete;
      if (printGraphqlErrors(errors)) {
        return;
      } else {
        toastMessage({
          message: `${currentIncident.name} has been deleted.`,
          type: 'info',
        });
      }

      dispatch(deleteIncidentAction({ incidentId: currentIncident.id }));

      if (analysisId) {
        dispatch(deleteAnalysisIncidentAction({ incident: currentIncident }));
      } else {
        dispatch(
          deleteDictionaryItemAction({
            itemId: currentIncident.id,
            dictionaryType: DictionaryTypeEnum.CalendarIncident,
          }),
        );
      }

      handleClose();
    },
    [analysisId, currentIncident, dispatch, handleClose],
  );
  const [deleteIncident, { loading }] = useMutation<
    IIncidentDeleteMutation,
    IIncidentDeleteMutationVariables
  >(incidentDeleteMutation, {
    variables: {
      input: {
        incidentId: currentIncident.id,
      },
    },
    onCompleted: onDeleteEvent,
  });

  return (
    <ConfirmationModal
      open={true}
      onClose={handleClose}
      onSubmit={deleteIncident}
      header={'Delete Confirmation'}
      confirmActionLabel={'Delete'}
      disabled={loading}
      trackContext="incidents"
      trackCancelAction="delete - cancel"
      trackConfirmAction="delete - confirm"
    >
      Are you sure you want to permanently delete incident{' '}
      <strong>{currentIncident.name}</strong>? This action cannot be undone.
    </ConfirmationModal>
  );
});
